/**
 * TotNghiepPageV2 — bảng điểm tốt nghiệp: tra cứu và nhập tay.
 *
 * Song song với màn nhập bằng AI: không phải tờ nào cũng có ảnh, và có ô AI đọc
 * không ra. Đây là nơi nhìn toàn bộ bảng điểm của một khóa, thấy ngay ai còn
 * thiếu môn nào, và gõ bù.
 *
 * Cột NGUỒN không phải trang trí. Một con số AI đọc sai trông y hệt một con số
 * người gõ đúng; không có cột này thì không ai soi lại được ô nào đáng ngờ.
 *
 * Sửa tại ô rồi PATCH ngay từng ô — không có nút "Lưu" tổng, vì một bảng vài
 * trăm dòng mà lưu cả bảng thì một ô hỏng là hỏng cả lần lưu.
 *
 * Chọn KHOÁ (21/10/2026): dropdown các khoá của trung tâm; chọn một khoá thì
 * bảng hiện ngay MỌI học viên của khoá (từ `drivings`), kể cả người chưa có bản
 * ghi điểm. Bản ghi chỉ được tạo khi gõ ô đầu tiên của người đó (POST rồi
 * PATCH) — xem thôi thì không tạo gì. Không chọn khoá = danh sách bản ghi đã có
 * như trước. Logic thuần ở `utils/totNghiepKhoa.js`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Col, Form, Modal, Row, Spinner, Table,
} from 'react-bootstrap';
import { AgGridReact } from 'ag-grid-react';
import { toast } from 'react-toastify';
import totNghiepApi from '../../services/totNghiep';
import V2PageLayout from '../../components/v2/V2PageLayout';
import {
  canTaoTruoc, locHocVien, lyDoKhongDongBo, nhanKhoa, taoBoTaoMotLan,
} from '../../utils/totNghiepKhoa';
import { O_DIEM_BANG, oHienThi } from '../../utils/totNghiepCotDiem';

/**
 * Tám ô điểm (0–100) trong dữ liệu — nhãn đầy đủ cho phần đối chiếu GetFly.
 * Bảng chỉ hiện sáu cột (`O_DIEM_BANG`): Luật / Mô phỏng gộp L1-L2.
 */
const O_DIEM = [
  ['lyThuyetL1', 'Luật L1'],
  ['lyThuyetL2', 'Luật L2'],
  ['moPhongL1', 'Mô phỏng L1'],
  ['moPhongL2', 'Mô phỏng L2'],
  ['chuChi', 'Chữ chi'],
  ['hinh', 'Sa hình'],
  ['duong', 'Đường'],
  ['diemTbThucHanhKtkh', 'TB thực hành - KTKH'],
];
const O_KET_LUAN = [['chuChi', 'KL chữ chi'], ['hinh', 'KL sa hình'], ['duong', 'KL đường']];
/** Chuỗi tự do, gõ sao lưu vậy — không suy ra từ điểm hay từ ketLuan. */
const O_KET_QUA = [
  ['ktra5Mon', 'KQKT 5 môn'],
  ['moPhong', 'KQ mô phỏng'],
  ['ktraThucHanh', 'KQ KT thực hành'],
];
/** Số, KHÔNG chặn trần 100 — khái niệm khác hẳn cột "Sa hình" (điểm) ở trên. */
const O_SA_HINH = [
  ['thoiGian', 'Sa hình - thời gian'],
  ['quangDuong', 'Sa hình - quãng đường'],
];

const NGUON_NHAN = {
  ai: 'AI', tay: 'Tay', tinh: 'Tự tính', '': '',
};
const NGUON_MAU = { ai: 'info', tay: 'secondary', tinh: 'success' };

/**
 * Nhãn người đọc cho một `localKey` trong kết quả check GetFly — khớp đúng
 * dạng key của `GETFLY_FIELD_MAP` bên backend: có tiền tố `diem.`/`ketQua.`/
 * `saHinh.`, riêng `ngayKtThucHanh` là field gốc nên không có tiền tố.
 */
const NHAN_O_GETFLY = {
  ...Object.fromEntries(O_DIEM.map(([k, nhan]) => [`diem.${k}`, nhan])),
  ngayKtThucHanh: 'Ngày KT thực hành',
  ...Object.fromEntries(O_KET_QUA.map(([k, nhan]) => [`ketQua.${k}`, nhan])),
  ...Object.fromEntries(O_SA_HINH.map(([k, nhan]) => [`saHinh.${k}`, nhan])),
};
const nhanO = (localKey) => NHAN_O_GETFLY[localKey] || localKey;

const fmtNgay = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (s || '');
};

export default function TotNghiepPageV2() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [khoaHoc, setKhoaHoc] = useState('');
  const [thieuDiem, setThieuDiem] = useState(false);
  const [tong, setTong] = useState(0);

  /* ── Chọn khoá ───────────────────────────────────────────────────────── */
  // '' = chế độ cũ: danh sách bản ghi đã có, lọc khoá bằng ô chữ tự do.
  const [dsKhoa, setDsKhoa] = useState([]);
  const [courseId, setCourseId] = useState('');
  const [rowsKhoa, setRowsKhoa] = useState([]);

  const loadDsKhoa = useCallback(async () => {
    try {
      const res = await totNghiepApi.listKhoa();
      setDsKhoa(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    }
  }, []);

  useEffect(() => { loadDsKhoa(); }, [loadDsKhoa]);

  // Tải cả khoá một lần (~25–120 HV); tìm và lọc thiếu điểm làm tại chỗ, không
  // gọi lại server mỗi lần gõ.
  const loadKhoa = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    try {
      const res = await totNghiepApi.listHocVienKhoa(courseId);
      setRowsKhoa(res.data?.docs || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
      setRowsKhoa([]);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  const loadCu = useCallback(async () => {
    setLoading(true);
    try {
      const res = await totNghiepApi.listDiem({
        q: q || undefined,
        khoaHoc: khoaHoc || undefined,
        thieuDiem: thieuDiem || undefined,
        limit: 200,
      });
      setRows(res.data?.docs || []);
      setTong(res.data?.totalDocs || 0);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [q, khoaHoc, thieuDiem]);

  const load = courseId ? loadKhoa : loadCu;

  useEffect(() => { load(); }, [load]);

  const rowsHien = useMemo(
    () => (courseId ? locHocVien(rowsKhoa, { q, thieuDiem }) : rows),
    [courseId, rowsKhoa, rows, q, thieuDiem],
  );

  /**
   * Mỗi HV chỉ một POST tạo bản ghi đang chạy: gõ hai ô liền nhau của cùng một
   * người chưa có bản ghi thì ô thứ hai chờ chung lần tạo của ô thứ nhất.
   */
  const taoMotLan = useMemo(() => taoBoTaoMotLan(totNghiepApi.createDiem), []);

  /**
   * Lưu ngay từng ô. Ô nào sửa thì nguồn của ô đó chuyển sang "Tay".
   * Dòng chưa có bản ghi (chỉ có ở chế độ chọn khoá) → tạo bản ghi trước rồi
   * mới PATCH.
   */
  const luuO = useCallback(async (row, body) => {
    try {
      let id = row?._id;
      if (canTaoTruoc(row)) {
        if (!row?.drivingId) throw new Error('Dòng này không gắn với học viên nào');
        id = await taoMotLan(row.drivingId);
        loadDsKhoa(); // số "có điểm" trong dropdown vừa đổi
      }
      await totNghiepApi.updateDiem(id, body);
      toast.success('Đã lưu');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
      await load(); // kéo lại giá trị thật, đừng để bảng hiện thứ chưa lưu được
    }
  }, [load, taoMotLan, loadDsKhoa]);

  /* ── Đồng bộ GetFly ──────────────────────────────────────────────────── */
  // Hai bước: check (chỉ đọc, không ghi gì) rồi mới sync — xung đột phải được
  // xác nhận từng ô một, không có xác nhận "đồng ý hết".
  const [gfRow, setGfRow] = useState(null);
  const [gfDangCheck, setGfDangCheck] = useState(false);
  const [gfKetQua, setGfKetQua] = useState(null);
  const [gfDongY, setGfDongY] = useState(() => new Set());
  const [gfDangGhi, setGfDangGhi] = useState(false);

  const moGetFly = useCallback(async (row) => {
    setGfRow(row);
    setGfKetQua(null);
    setGfDongY(new Set());
    setGfDangCheck(true);
    try {
      const res = await totNghiepApi.getflySyncCheck(row._id);
      setGfKetQua(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
      setGfRow(null);
    } finally {
      setGfDangCheck(false);
    }
  }, []);

  const dongGetFly = useCallback(() => {
    setGfRow(null);
    setGfKetQua(null);
    setGfDongY(new Set());
  }, []);

  const toggleDongY = useCallback((localKey) => {
    setGfDongY((cu) => {
      const moi = new Set(cu);
      if (moi.has(localKey)) moi.delete(localKey); else moi.add(localKey);
      return moi;
    });
  }, []);

  const xungDot = gfKetQua?.xungDot || [];
  const chuaDuyetHet = xungDot.length > 0 && xungDot.some((x) => !gfDongY.has(x.localKey));

  const ghiGetFly = useCallback(async () => {
    if (!gfRow || chuaDuyetHet || gfDangGhi) return;
    setGfDangGhi(true);
    try {
      const res = await totNghiepApi.getflySync(gfRow._id, [...gfDongY]);
      const r = res.data || {};
      toast.success(`Đã ghi ${r.ghi ?? 0} trường lên GetFly${r.boQuaRong ? `, bỏ qua ${r.boQuaRong} ô trống` : ''}`);
      dongGetFly();
      await load();
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error('Còn xung đột chưa xác nhận — kiểm tra lại trước khi ghi');
      } else {
        toast.error(e?.response?.data?.message || e.message);
      }
    } finally {
      setGfDangGhi(false);
    }
  }, [gfRow, gfDongY, chuaDuyetHet, gfDangGhi, dongGetFly, load]);

  const columnDefs = useMemo(() => [
    { field: 'registrationCode', headerName: 'Mã học viên', width: 190, pinned: 'left' },
    {
      field: 'hoTen',
      headerName: 'Họ tên',
      width: 250,
      pinned: 'left',
      // Chỉ dòng của chế độ chọn khoá mang cờ coGetfly; false = chắc chắn chưa liên kết.
      cellRenderer: (p) => (
        <span>
          {p.data.hoTen}
          {p.data.coGetfly === false && (
            <Badge bg='warning' text='dark' className='ms-1' style={{ fontSize: '0.65rem' }}>
              Chưa liên kết GetFly
            </Badge>
          )}
        </span>
      ),
    },
    {
      field: 'ngaySinh', headerName: 'Ngày sinh', width: 120, valueGetter: (p) => fmtNgay(p.data.ngaySinh),
    },
    { field: 'khoaHoc', headerName: 'Khóa', width: 110 },
    { field: 'hang', headerName: 'Hạng', width: 80 },

    ...O_DIEM_BANG.map((cot) => ({
      headerName: cot.nhan,
      headerTooltip: cot.choGo ? undefined : 'Tự tính = (Chữ chi + Sa hình + Đường) / 30 khi đủ ba môn',
      width: cot.choGo ? 115 : 150,
      editable: cot.choGo,
      cellDataType: false,
      valueGetter: (p) => oHienThi(p.data.diem, cot).value,
      valueSetter: (p) => {
        const moi = p.newValue === '' || p.newValue === null ? null : Number(p.newValue);
        if (moi !== null && (!Number.isFinite(moi) || moi < 0 || moi > 100)) {
          toast.error('Điểm phải là số từ 0 đến 100');
          return false;
        }
        luuO(p.data, { diem: { [cot.key]: moi } });
        return true;
      },
      // Nguồn hiện ngay trong ô: số nào AI đọc, số nào người gõ, số nào tự tính.
      cellRenderer: (p) => {
        const o = oHienThi(p.data.diem, cot);
        if (o.value === null) return <span className='text-muted'>—</span>;
        return (
          <span>
            {o.value}
            {o.laL2 && (
              <Badge bg='warning' text='dark' className='ms-1' style={{ fontSize: '0.65rem' }} title='Điểm thi lần 2'>
                L2
              </Badge>
            )}
            {o.nguon && (
              <Badge bg={NGUON_MAU[o.nguon] || 'secondary'} className='ms-1' style={{ fontSize: '0.65rem' }}>
                {NGUON_NHAN[o.nguon]}
              </Badge>
            )}
          </span>
        );
      },
    })),

    ...O_KET_LUAN.map(([key, nhan]) => ({
      headerName: nhan,
      width: 120,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: ['', 'dat', 'khongDat'] },
      valueGetter: (p) => p.data.ketLuan?.[key] || '',
      valueSetter: (p) => { luuO(p.data, { ketLuan: { [key]: p.newValue || '' } }); return true; },
      cellRenderer: (p) => {
        const v = p.data.ketLuan?.[key];
        if (v === 'dat') return <Badge bg='success'>Đạt</Badge>;
        if (v === 'khongDat') return <Badge bg='danger'>Không đạt</Badge>;
        return <span className='text-muted'>—</span>;
      },
    })),

    // Chuỗi tự do — gõ gì lưu nấy, không suy ra từ điểm hay ketLuan.
    ...O_KET_QUA.map(([key, nhan]) => ({
      headerName: nhan,
      width: 150,
      editable: true,
      valueGetter: (p) => p.data.ketQua?.[key]?.value || '',
      valueSetter: (p) => {
        const moi = String(p.newValue ?? '').trim();
        luuO(p.data, { ketQua: { [key]: moi || null } });
        return true;
      },
    })),

    {
      headerName: 'Ngày KT thực hành',
      width: 160,
      editable: true,
      valueGetter: (p) => p.data.ngayKtThucHanh || '',
      valueSetter: (p) => { luuO(p.data, { ngayKtThucHanh: p.newValue || '' }); return true; },
      valueFormatter: (p) => fmtNgay(p.value),
    },
    {
      headerName: 'Giờ đào tạo',
      width: 120,
      valueGetter: (p) => p.data.daoTao?.tongGio ?? null,
    },
    {
      headerName: 'Km đào tạo',
      width: 120,
      valueGetter: (p) => p.data.daoTao?.tongKm ?? null,
    },

    // Sân tập lái — khác hẳn điểm "Sa hình" ở trên, không chặn trần 100.
    ...O_SA_HINH.map(([key, nhan]) => ({
      headerName: nhan,
      width: 150,
      editable: true,
      cellDataType: false,
      valueGetter: (p) => p.data.saHinh?.[key]?.value ?? null,
      valueSetter: (p) => {
        const moi = p.newValue === '' || p.newValue === null ? null : Number(p.newValue);
        if (moi !== null && (!Number.isFinite(moi) || moi < 0)) {
          toast.error('Phải là số và không âm');
          return false;
        }
        luuO(p.data, { saHinh: { [key]: moi } });
        return true;
      },
    })),

    {
      headerName: 'GetFly',
      width: 130,
      cellRenderer: (p) => {
        const lyDo = lyDoKhongDongBo(p.data);
        if (lyDo) {
          // Nút bị khoá không bắt chuột nên title đặt ở span bọc ngoài.
          return (
            <span title={lyDo}>
              <Button size='sm' variant='outline-secondary' disabled style={{ pointerEvents: 'none' }}>
                Đồng bộ
              </Button>
            </span>
          );
        }
        return (
          <Button size='sm' variant='outline-primary' onClick={() => moGetFly(p.data)}>
            Đồng bộ
          </Button>
        );
      },
    },
  ], [luuO, moGetFly]);

  return (
    <V2PageLayout
      title={(
        <>
          Bảng điểm tốt nghiệp{' '}
          <span className='text-muted fs-6'>
            ({courseId ? `${rowsHien.length}/${rowsKhoa.length}` : tong} học viên)
          </span>
        </>
      )}
      filters={(
        <Row className='g-2 align-items-center'>
          <Col xs='auto'>
            <Form.Select
              size='sm'
              value={courseId}
              onChange={(e) => { setRowsKhoa([]); setCourseId(e.target.value); }}
              style={{ width: 320 }}
              aria-label='Chọn khoá'
            >
              <option value=''>— Không chọn khoá: các bản ghi điểm đã có —</option>
              {dsKhoa.map((k) => (
                <option key={k._id} value={k._id}>{nhanKhoa(k)}</option>
              ))}
            </Form.Select>
          </Col>
          <Col xs='auto'>
            <Form.Control
              size='sm'
              placeholder='Tìm tên hoặc mã học viên'
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ width: 240 }}
            />
          </Col>
          {!courseId && (
            <Col xs='auto'>
              <Form.Control
                size='sm'
                placeholder='Khóa (vd C1-K24)'
                value={khoaHoc}
                onChange={(e) => setKhoaHoc(e.target.value)}
                style={{ width: 170 }}
              />
            </Col>
          )}
          <Col xs='auto'>
            <Form.Check
              type='checkbox'
              label='Chỉ người còn thiếu điểm'
              checked={thieuDiem}
              onChange={(e) => setThieuDiem(e.target.checked)}
            />
          </Col>
        </Row>
      )}
      actions={(
        <>
          {loading && <Spinner size='sm' animation='border' className='me-2' />}
          <Button
            size='sm'
            variant='outline-secondary'
            onClick={() => { load(); loadDsKhoa(); }}
            disabled={loading}
          >
            Tải lại
          </Button>
        </>
      )}
    >
      <AgGridReact
        rowData={rowsHien}
        columnDefs={columnDefs}
        defaultColDef={{ resizable: true, sortable: true, suppressHeaderMenuButton: true }}
        getRowId={(p) => (p.data.drivingId ? `hv-${p.data.drivingId}` : `rec-${p.data._id}`)}
        overlayNoRowsTemplate={courseId
          ? '<span class="ag-overlay-loading-center">Không có học viên nào khớp</span>'
          : '<span class="ag-overlay-loading-center">Chưa có bản ghi điểm nào</span>'}
        stopEditingWhenCellsLoseFocus
      />

      <Modal show={!!gfRow} onHide={dongGetFly} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Đồng bộ GetFly — {gfRow?.hoTen} ({gfRow?.registrationCode})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {gfDangCheck && (
            <div className='text-center py-4'>
              <Spinner animation='border' className='me-2' /> Đang kiểm tra dữ liệu trên GetFly…
            </div>
          )}

          {!gfDangCheck && gfKetQua && (
            <>
              {Object.keys(gfKetQua.ghi || {}).length > 0 && (
                <Alert variant='light' className='border small mb-2'>
                  <strong>Sẽ ghi {Object.keys(gfKetQua.ghi).length} trường</strong> — không trùng với dữ liệu
                  hiện có trên GetFly (trống hoặc giống nhau).
                </Alert>
              )}

              {xungDot.length > 0 && (
                <>
                  <Alert variant='warning' className='small mb-2'>
                    <strong>{xungDot.length} trường trên GetFly đang có giá trị KHÁC</strong> — tick xác nhận
                    từng trường muốn ghi đè. Trường chưa tick sẽ KHÔNG được ghi.
                  </Alert>
                  <Table size='sm' bordered className='mb-3'>
                    <thead>
                      <tr>
                        <th style={{ width: 40 }} />
                        <th>Trường</th>
                        <th>Giá trị ở đây</th>
                        <th>Giá trị trên GetFly</th>
                      </tr>
                    </thead>
                    <tbody>
                      {xungDot.map((x) => (
                        <tr key={x.localKey}>
                          <td className='text-center'>
                            <Form.Check
                              type='checkbox'
                              checked={gfDongY.has(x.localKey)}
                              onChange={() => toggleDongY(x.localKey)}
                            />
                          </td>
                          <td>{nhanO(x.localKey)}</td>
                          <td>{String(x.localValue)}</td>
                          <td className='text-danger'>{String(x.getflyValue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              {(gfKetQua.boQuaRong || []).length > 0 && (
                <div className='small text-muted mb-2'>
                  Bỏ qua {gfKetQua.boQuaRong.length} ô đang trống (không ghi đè GetFly bằng chuỗi rỗng):{' '}
                  {gfKetQua.boQuaRong.map((x) => nhanO(x.localKey)).join(', ')}
                </div>
              )}

              {(gfKetQua.chuaRoMaField || []).length > 0 && (
                <Alert variant='secondary' className='small mb-0'>
                  Chưa đồng bộ được — cần đo field GetFly trước:{' '}
                  {gfKetQua.chuaRoMaField.map((x) => nhanO(x.localKey)).join(', ')}
                </Alert>
              )}

              {!xungDot.length && !Object.keys(gfKetQua.ghi || {}).length && (
                <div className='text-muted small'>Không có trường nào để ghi.</div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='outline-secondary' onClick={dongGetFly}>Đóng</Button>
          <Button
            variant='primary'
            disabled={gfDangCheck || !gfKetQua || gfDangGhi || chuaDuyetHet
              || (!Object.keys(gfKetQua?.ghi || {}).length && !xungDot.length)}
            onClick={ghiGetFly}
          >
            {gfDangGhi ? <><Spinner size='sm' animation='border' className='me-2' />Đang ghi…</> : 'Ghi lên GetFly'}
          </Button>
        </Modal.Footer>
      </Modal>
    </V2PageLayout>
  );
}
